import { ConfigJSON } from "@tc/configurability/types"
import {
  REQUEST_CARRIER_NAME,
  REQUEST_CONVEYANCE_REFERENCE,
  REQUEST_FINAL_DESTINATION,
  REQUEST_LOADING_PORT,
  REQUEST_TRANSPORT_LOCAL_CARRIER,
  REQUEST_TRANSPORT_MODE_CODE,
  TRANSFER_THIRD_PARTY_IDENTIFIER,
} from "@tc/field-anchor/fields/fuid"
import { isCoreLookupObject } from "@tc/field-anchor"
import { ExportRequestType } from "@tc/graphql-server"
import { CoreLookup } from "@tc/ui-dfe/query/types"
import {
  EXPORT_EXTRACT_OTHER_REQUEST,
  EXPORT_EXTRACT_PHYTO_REQUEST,
  EXPORT_EXTRACT_WINE_REQUEST,
} from "@tc/ui-dfe/utils/constants"
import { fieldPath } from "@tc/ui-dfe/utils/helper"
import {
  ConfigurableProductType,
  ConfigurableRequestFormType,
  ExistingReferencedDocumentField,
  ReferencedDocumentField,
  SavedProductsDataModel,
} from "@tc/ui-dfe/utils/types"
import { z } from "zod"
import { SectionActiveTab } from "./useFormLayout/useFormLayout"
import { filterSchema } from "@tc/ui-dfe/utils/validations"
export type {
  ConfigurableProcessType,
  ConfigurableProductType,
  ConfigurableRequestFormType,
  ConfigurableSourceType,
  ExistingReferencedDocumentField,
  ReferencedDocumentField,
  SavedProductsDataModel,
} from "@tc/ui-dfe/utils/types"

// Validation schemas
const ExistingDocumentSchema = z.object({
  id: z.string().min(1),
  _doc_fileName: z.string().min(1),
  docContentType: z.string(),
  docComments: z.string(),
  _isExisting: z.literal(true),
  _documentId: z.string().min(1),
})

const NewDocumentSchema = z.object({
  id: z.string().min(1),
  _doc_fileName: z.string().min(1),
  docContentType: z.string(),
  docComments: z.string(),
  _isExisting: z.literal(false).optional(),
  _documentId: z.never().optional(),
})

export const ReferencedDocumentSchema = z.union([
  ExistingDocumentSchema,
  NewDocumentSchema,
])

export const validateReferencedDocument = (
  doc: unknown,
): doc is ReferencedDocumentField => {
  return ReferencedDocumentSchema.safeParse(doc).success
}

// Backend payload types
export interface SupportingDocumentPayload {
  comment: string
  documentReferenceNumber: string
  b2gFile: {
    extensionType: string
    name: string
  }
  id?: string // Only for existing documents
}

export interface SelectedProductInfoProps {
  certificateNumber: string
  productName: string
  sourceProductId?: string
  productSequenceNumber?: number
  transferRequestId?: string
}

export type RemoveSourceIdType = {
  prdId: string
  prdSrcId: string[]
}

/**
 * Filters out inherited supporting documents from a product's supporting documents
 * when sources are being removed for that product. This prevents the frontend from
 * re-sending inherited docs that the backend has already cleaned up.
 */
export const filterInheritedDocsForRemovedSources = (
  supportingDocuments: Array<{ isInherited?: boolean }> | undefined,
  productId: string,
  removedSources: RemoveSourceIdType[],
): Array<{ isInherited?: boolean }> => {
  if (!supportingDocuments) return []
  const hasRemovedSources = removedSources.some((rs) => rs.prdId === productId)
  if (!hasRemovedSources) return supportingDocuments
  return supportingDocuments.filter((doc) => !doc.isInherited)
}

export type RequestType = "export" | "transfer"

export const DefaultProductValues: ConfigurableProductType = {
  id: "",
  process: [],
  sources: [],
  container: [],
  packaging: [{}],
}

export const filterSavedProducts = (productFields: ConfigurableProductType[]) =>
  productFields.filter((product) => product.id) as SavedProductsDataModel[]

export const getFieldsFromRecipe = (recipe: ConfigJSON | undefined) => {
  return recipe?.products?.sections
    .find(({ id }) => id === "classifications")
    ?.children.map(({ rows }) =>
      rows?.map(({ fields }) => fields?.map(({ fieldRef }) => fieldRef)),
    )
    .flat(4)
    .filter((fieldRef) => typeof fieldRef === "string")
}

export const getClassificationFields = (
  recipe: ConfigJSON | undefined,
): Record<string, string> => {
  const result: Record<string, string> = {}

  const classificationSection = recipe?.products?.sections.find(
    (section) =>
      section.id === "classification" || section.id === "classifications",
  )

  if (!classificationSection?.children) {
    return result
  }

  classificationSection.children.forEach((child) => {
    if (child.rows) {
      child.rows.forEach((row) => {
        // When a row has multiple fields, the row.label is a combined label
        // (e.g., "Field A and Field B"). We need to map each field's fieldRef
        // to the row label so all fields in the row can be looked up.
        if (row.label && row.fields?.length) {
          row.fields.forEach((field) => {
            if (field.fieldRef) {
              result[field.fieldRef] = row.label as string
            }
          })
        }
      })
    }
  })

  return result
}

// Document processing utilities
export const mapBackendDocumentToForm = (
  doc: {
    __typename?: string
    id?: string | null
    documentReferenceNumber?: string | null
    comment?: string | null
    b2gFile?: { name?: string | null; extensionType?: string | null } | null
  } | null,
): ReferencedDocumentField => {
  if (!doc?.documentReferenceNumber) {
    throw new Error("Document reference number is required")
  }

  return {
    id: doc.documentReferenceNumber,
    _doc_fileName: doc.b2gFile?.name || "",
    docContentType: doc.b2gFile?.extensionType || "",
    docComments: doc.comment || "",
    _isExisting: true,
    _documentId: doc.id || "",
  } as ExistingReferencedDocumentField
}

export const mapFormDocumentToPayload = (
  doc: ReferencedDocumentField,
): SupportingDocumentPayload => {
  const payload: SupportingDocumentPayload = {
    comment: doc.docComments,
    documentReferenceNumber: doc.id,
    b2gFile: {
      extensionType: doc.docContentType,
      name: doc._doc_fileName,
    },
  }

  // Only include ID for existing documents
  if (doc._isExisting && doc._documentId) {
    payload.id = doc._documentId
  }

  return payload
}

export const getSourcesData = (requestType?: RequestType) => {
  const fields = [
    {
      label: "Item Description",
      fieldRef: "itemDescription",
      required: false,
      type: "text",
    },
    ...(requestType === "export"
      ? [
          {
            label: "NZHS Code",
            fieldRef: "attributes.NZHSCode",
            required: false,
            type: "text",
          },
        ]
      : []),
    {
      label: "Origin",
      fieldRef: "attributes.origin",
      required: false,
      type: "text",
    },
    {
      label: "Net Weight",
      fieldRef: "attributes.netWeight",
      required: false,
      type: "text",
    },
    {
      label: "Net Volume",
      fieldRef: "attributes.netVolume",
      required: false,
      type: "text",
    },
    {
      label: "Intended Use",
      fieldRef: "attributes.intendedUse",
      required: false,
      type: "text",
    },
    {
      label: "Inspection Type",
      fieldRef: "attributes.inspectionType",
      required: false,
      type: "text",
    },
    ...(requestType === "transfer"
      ? [
          {
            label: "NZHS Code",
            fieldRef: "attributes.NZHSCode",
            required: false,
            type: "text",
          },
        ]
      : []),
    {
      label: "Species",
      fieldRef: "attributes.speciesCommonName",
      required: false,
      type: "text",
    },
    {
      label: "Scientific Name",
      fieldRef: "attributes.scientificName",
      required: false,
      type: "text",
    },
    {
      label: "Eligibility Countries",
      fieldRef: "eligibilityCountries",
      required: false,
      type: "text",
    },
    {
      label: "Expiry Date From",
      fieldRef: "attributes.expiryDateFrom",
      required: false,
      type: "date",
    },
    {
      label: "Expiry Date To",
      fieldRef: "attributes.expiryDateTo",
      required: false,
      type: "date",
    },
  ]

  return fields
}

export const getPackagingFields = () => [
  {
    label: "Quantity",
    fieldRef: "quantity",
    required: false,
    type: "text",
  },
  {
    label: "Package Type",
    fieldRef: "packageType.name",
    required: false,
    type: "text",
  },
  {
    label: "Nominal Gross Weight",
    fieldRef: "nominalGrossWeight",
    required: false,
    type: "text",
  },
  {
    label: "Nominal Gross Volume",
    fieldRef: "nominalGrossVolume",
    required: false,
    type: "text",
  },
  {
    label: "Item Marks Outer",
    fieldRef: "itemMarksOuter",
    required: false,
    type: "text",
  },
  {
    label: "Outermost Number of Packages",
    fieldRef: "outermostNumberOfPacakges",
    required: false,
    type: "text",
  },
]

export const getExportExtractSuccessMessage = (
  exportRequestType: ExportRequestType,
) => {
  switch (exportRequestType) {
    case ExportRequestType.PlantsProducts:
      return EXPORT_EXTRACT_PHYTO_REQUEST
    case ExportRequestType.Wine:
      return EXPORT_EXTRACT_WINE_REQUEST
    default:
      return EXPORT_EXTRACT_OTHER_REQUEST
  }
}

export const sortBySequenceNumber = <
  T extends { sequenceNumber?: string | number | null },
>(
  items: T[],
): T[] => {
  return [...items].sort(
    (a, b) => Number(a?.sequenceNumber ?? 0) - Number(b?.sequenceNumber ?? 0),
  )
}

export const prepareEditApprovedTransferPayload = (
  formValues: ConfigurableRequestFormType,
  transferId: string,
) => {
  const thirdPartyAccess =
    (formValues[fieldPath(TRANSFER_THIRD_PARTY_IDENTIFIER)] as CoreLookup[])
      ?.filter((item): item is CoreLookup => item != null && item.id != null)
      ?.map((item) => ({
        id: item.id,
        name: item.name ?? "",
      })) || []

  return {
    transferId,
    transport: {
      carrierName:
        (formValues[fieldPath(REQUEST_CARRIER_NAME)] as string) || "",
      conveyanceReference:
        (formValues[fieldPath(REQUEST_CONVEYANCE_REFERENCE)] as string) || "",
      finalDestination: isCoreLookupObject(
        formValues[fieldPath(REQUEST_FINAL_DESTINATION)],
      )
        ? {
            id: (formValues[fieldPath(REQUEST_FINAL_DESTINATION)] as CoreLookup)
              .id,
            name:
              (formValues[fieldPath(REQUEST_FINAL_DESTINATION)] as CoreLookup)
                .name ?? "",
          }
        : null,
      loadingPort: {
        id:
          (formValues[fieldPath(REQUEST_LOADING_PORT)] as CoreLookup)?.id || "",
        name:
          (formValues[fieldPath(REQUEST_LOADING_PORT)] as CoreLookup)?.name ||
          "",
      },
      localCarrier:
        (formValues[fieldPath(REQUEST_TRANSPORT_LOCAL_CARRIER)] as string) ||
        "",
      transportMode: {
        id:
          (formValues[fieldPath(REQUEST_TRANSPORT_MODE_CODE)] as CoreLookup)
            ?.id || "",
        name:
          (formValues[fieldPath(REQUEST_TRANSPORT_MODE_CODE)] as CoreLookup)
            ?.name || "",
      },
    },
    thirdPartyAccess,
  }
}

export function shiftValuesToLeft(obj: SectionActiveTab, startKey: number) {
  const keys = Object.keys(obj)
    .map(Number)
    .sort((a, b) => a - b)

  const startIdx = keys.indexOf(Number(startKey))
  if (startIdx === -1) return obj

  for (let i = startIdx; i < keys.length - 1; i++) {
    obj[keys[i]] = obj[keys[i + 1]]
  }
  const lastKey = keys.at(-1)
  if (lastKey !== undefined) delete obj[lastKey]
  return obj
}

//Transform filter state for exports & transfer requests download csv  mutations's payload
export const transformFilterState = (filters: unknown[]): string => {
  const validatedFilters = z.array(filterSchema).parse(filters)
  return validatedFilters
    .filter((filter) => filter.refinements.length > 0)
    .map((filter) => {
      const attribute = filter.attribute
      const filterType = filter.refinements[0].type
      if (filterType === "numeric") {
        const [first, second] = filter.refinements
        if (first && second) {
          return `${attribute}:=[${first.value}..${second.value}]`
        }
        if (second) {
          const op = second.operator || "<="
          return `${attribute}:${op}${second.value}`
        }
        if (first) {
          const op = first.operator || ">="
          return `${attribute}:${op}${first.value}`
        }
      }
      const refinements = filter.refinements
        .map((i) => "`" + i.value + "`")
        .join(",")
      return `${attribute}:=[${refinements}]`
    })
    .join(" && ")
}
